import { z } from "zod";
import { BaseModelFields } from "./base-model";

// Enums
export enum FulfillmentMethod {
  PICKUP = "PICKUP",
  DELIVERY_CITY = "DELIVERY_CITY",
  DELIVERY_PROVINCE = "DELIVERY_PROVINCE",
  UNKNOWN = "UNKNOWN",
}

export enum FulfillmentCarrier {
  VIREAK_BUNTHAM = "VIREAK_BUNTHAM",
  JNT = "JNT",
}

// Main Fulfillment interface
export interface Fulfillment extends BaseModelFields {
  orderId: string; // Long -> string for BigInt handling
  method: FulfillmentMethod;
  carrier: FulfillmentCarrier;
  price: string; // BigDecimal -> string for precision handling
  trackingNumber?: string | null;
}

// Request/Response DTOs (single source of truth for API contracts)
export interface FulfillmentCreateRequest {
  orderId: string;
  method: FulfillmentMethod;
  carrier: FulfillmentCarrier;
  price: string; // BigDecimal -> string for precision handling
}

export interface FulfillmentUpdateRequest {
  orderId?: string;
  method?: FulfillmentMethod;
  carrier?: FulfillmentCarrier;
  price?: string; // BigDecimal -> string for precision handling
}

export interface FulfillmentUpdateFeeRequest {
  price: number;
}

// Fulfillment form schema for create operations
export const fulfillmentCreateSchema = z.object({
  orderId: z.string().min(1, "Order ID is required"),
  method: z.nativeEnum(FulfillmentMethod, {
    required_error: "Fulfillment method is required",
  }),
  carrier: z.nativeEnum(FulfillmentCarrier, {
    required_error: "Fulfillment carrier is required",
  }),
  price: z
    .string()
    .min(1, "Price is required")
    .refine(
      (val) => !isNaN(parseFloat(val)) && parseFloat(val) >= 0,
      "Price must be a valid number greater than or equal to 0"
    ),
});

// Fulfillment form schema for update operations
export const fulfillmentUpdateSchema = z.object({
  orderId: z
    .string()
    .min(1, "Order ID is required")
    .refine(
      (val) => !isNaN(parseFloat(val)) && parseFloat(val) >= 0,
      "Order ID must be a valid number greater than or equal to 0"
    )
    .optional(),
  method: z.nativeEnum(FulfillmentMethod).optional(),
  carrier: z.nativeEnum(FulfillmentCarrier).optional(),
  price: z
    .string()
    .refine(
      (val) => !isNaN(parseFloat(val)) && parseFloat(val) >= 0,
      "Price must be a valid number greater than or equal to 0"
    )
    .optional(),
});

// Fulfillment form schema for update fee operations
export const fulfillmentUpdateFeeSchema = z.object({
  price: z.preprocess(
    (val) => (val === undefined || val === null ? 0 : val),
    z
      .number({
        required_error: "Price is required",
        invalid_type_error: "Price must be a number",
      })
      .min(0, "Price must be greater than or equal to 0")
      .refine(
        (val) => !isNaN(val) && isFinite(val),
        "Price must be a valid number"
      )
  ),
});

// Fulfillment form values types
export type FulfillmentCreateFormValues = z.infer<
  typeof fulfillmentCreateSchema
>;
export type FulfillmentUpdateFormValues = z.infer<
  typeof fulfillmentUpdateSchema
>;
export type FulfillmentUpdateFeeFormValues = z.infer<
  typeof fulfillmentUpdateFeeSchema
>;
